import React, { useEffect, useState } from 'react';
import { Socket } from 'socket.io-client';

interface GameHistoryEntry {
  id: string;
  player_x_username: string;
  player_o_username: string;
  winner: string | null;
  final_score_x: number;
  final_score_o: number;
  rounds_played: number;
  started_at: string;
  ended_at: string;
}

interface PlayerHistoryProps {
  socket: Socket | null;
  username: string;
  onClose: () => void;
}

export const PlayerHistory: React.FC<PlayerHistoryProps> = ({ socket, username, onClose }) => {
  const [history, setHistory] = useState<GameHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!socket || !username) return;

    // Solicitar historial
    socket.emit('get-history', username);

    // Escuchar respuesta
    socket.on('history-data', (data: GameHistoryEntry[]) => {
      setHistory(data);
      setLoading(false);
    });

    socket.on('history-error', (error: string) => {
      console.error('Error al cargar historial:', error);
      setLoading(false);
    });

    return () => {
      socket.off('history-data');
      socket.off('history-error');
    };
  }, [socket, username]);

  const getResult = (game: GameHistoryEntry): { text: string; color: string } => {
    if (!game.winner) return { text: 'EMPATE', color: 'text-neon-yellow' };
    
    const isPlayerX = game.player_x_username === username;
    const won = (isPlayerX && game.winner === 'X') || (!isPlayerX && game.winner === 'O');
    
    return won 
      ? { text: 'VICTORIA', color: 'text-neon-green' }
      : { text: 'DERROTA', color: 'text-neon-pink' };
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-retro-dark border-4 border-neon-cyan rounded-lg shadow-neon-cyan max-w-3xl w-full max-h-[calc(100dvh-2rem)] sm:max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-neon-cyan/20 border-b-2 border-neon-cyan p-4 flex justify-between items-center shrink-0">
          <h2 className="font-retro text-2xl text-neon-cyan animate-glow">
            📊 HISTORIAL - {username}
          </h2>
          <button
            onClick={onClose}
            className="ui-button text-neon-pink hover:text-neon-yellow transition-colors text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-8">
              <p className="font-retro text-neon-cyan animate-pulse">
                CARGANDO HISTORIAL...
              </p>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8">
              <p className="font-retro text-neon-yellow">
                NO HAY PARTIDAS JUGADAS
              </p>
              <p className="font-retro text-neon-cyan text-sm mt-2">
                ¡Juega tu primera partida!
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((game) => {
                const result = getResult(game);
                const opponent = game.player_x_username === username 
                  ? game.player_o_username 
                  : game.player_x_username;
                const myScore = game.player_x_username === username 
                  ? game.final_score_x 
                  : game.final_score_o;
                const opponentScore = game.player_x_username === username 
                  ? game.final_score_o 
                  : game.final_score_x;

                return (
                  <div
                    key={game.id}
                    className="bg-retro-dark/50 border-2 border-neon-cyan rounded-lg p-4 hover:bg-neon-cyan/10 transition-all"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <p className="font-retro text-neon-cyan text-sm">
                          VS <span className="text-neon-yellow">{opponent}</span>
                        </p>
                        <p className="font-retro text-xs text-gray-400 mt-1">
                          {formatDate(game.ended_at)}
                        </p>
                      </div>
                      <div className={`font-retro text-lg font-bold ${result.color}`}>
                        {result.text}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-center gap-4 mt-3">
                      <div className="text-center">
                        <p className="font-retro text-xs text-neon-cyan">TÚ</p>
                        <p className="font-retro text-3xl text-neon-green">{myScore}</p>
                      </div>
                      <div className="font-retro text-2xl text-gray-500">-</div>
                      <div className="text-center">
                        <p className="font-retro text-xs text-neon-cyan">RIVAL</p>
                        <p className="font-retro text-3xl text-neon-pink">{opponentScore}</p>
                      </div>
                    </div>

                    <div className="text-center mt-2">
                      <p className="font-retro text-xs text-gray-400">
                        {game.rounds_played} {game.rounds_played === 1 ? 'ronda' : 'rondas'} jugadas
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-neon-cyan/20 border-t-2 border-neon-cyan p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] text-center shrink-0">
          <button
            onClick={onClose}
            className="ui-button bg-neon-pink/30 hover:bg-neon-pink/50 border-2 border-neon-pink text-neon-pink hover:shadow-neon-pink"
          >
            CERRAR
          </button>
        </div>
      </div>
    </div>
  );
};
